import { Controller } from "@hotwired/stimulus"

// Generic "show one panel at a time" switcher behind a segmented control.
// Tabs and panels are matched by a shared data-key. Clicking a tab reveals the
// matching panel and hides the rest. Used on the homepage (keys are competition
// modes) and on leaderboards (keys are chart metrics: cost / speed). The
// dimension differs per page; the behavior is identical.
//
// Markup:
//   <div data-controller="chart-switcher">
//     <div role="tablist">
//       <button data-chart-switcher-target="tab" data-key="cost" data-active="true"
//               data-action="chart-switcher#switch" ...>Quality vs cost</button>
//       <button data-chart-switcher-target="tab" data-key="speed"
//               data-action="chart-switcher#switch" ...>Quality vs speed</button>
//     </div>
//     <div data-chart-switcher-target="panel" data-key="cost">...</div>
//     <div data-chart-switcher-target="panel" data-key="speed" hidden>...</div>
//   </div>
//
// The active tab carries data-active="true" so the view can style it server-side
// for the no-JS / pre-hydration paint; connect() reconciles to that tab.
const FADE_MS = 150

// Cross-switcher sync: switchers that share a non-empty sync-group reflect each
// other's selection (e.g. the homepage scatter and the skill gallery both keyed
// by competition mode). A switcher ignores a key it doesn't have, so groups with
// differing key sets stay smooth.
const SYNC_EVENT = "arena:mode-changed"

export default class extends Controller {
  static targets = ["tab", "panel", "caption"]
  static values = { syncGroup: { type: String, default: "" } }

  declare readonly tabTargets: HTMLElement[]
  declare readonly panelTargets: HTMLElement[]
  // Optional: elements whose text mirrors the active key. Each carries a
  // data-<key> attribute per key (e.g. data-cost / data-speed); on switch we
  // swap textContent to the active key's copy. Used by the arena "living
  // headline" so the kicker + meta line track the cost/speed selection.
  declare readonly captionTargets: HTMLElement[]
  declare readonly syncGroupValue: string

  private currentKey?: string
  // Cleanup for any in-flight crossfade, so rapid clicks can cancel/replace it.
  private fadeCleanup?: () => void
  private onSync?: (event: Event) => void

  connect() {
    // Initial paint: no outgoing panel to animate, so switch instantly.
    this.select(this.initialKey(), { animate: false })

    if (this.syncGroupValue) {
      this.onSync = (event: Event) => this.handleSync(event as CustomEvent)
      window.addEventListener(SYNC_EVENT, this.onSync)
    }
  }

  disconnect() {
    this.fadeCleanup?.()
    if (this.onSync) window.removeEventListener(SYNC_EVENT, this.onSync)
  }

  switch(event: Event) {
    const tab = event.currentTarget as HTMLElement
    const key = tab.dataset.key
    if (!key) return
    const anchorTop = tab.getBoundingClientRect().top
    this.select(key, { animate: true })
    this.broadcast(key)
    this.keepAnchored(tab, anchorTop)
  }

  // Keep the tapped control pinned in the viewport while panels — here AND in
  // synced switchers (the homepage scatter and skill gallery share a mode group,
  // and one may sit above the fold) — crossfade and resize, so switching modes
  // never scrolls the page out from under the user. Corrects across a short
  // settle window covering the crossfade and the lazy chart render; once heights
  // are stable the per-frame delta is 0, so it stops fighting any real scroll.
  // Only the user-clicked switcher anchors (synced targets don't), so anchors
  // never compete.
  private keepAnchored(anchor: HTMLElement, startTop: number) {
    const correct = () => {
      const delta = anchor.getBoundingClientRect().top - startTop
      if (Math.abs(delta) > 0.5) window.scrollBy(0, delta)
    }
    if (this.prefersReducedMotion()) {
      requestAnimationFrame(correct)
      return
    }
    const start = performance.now()
    const settle = () => {
      correct()
      if (performance.now() - start < 420) requestAnimationFrame(settle)
    }
    requestAnimationFrame(settle)
  }

  private broadcast(key: string) {
    if (!this.syncGroupValue) return
    window.dispatchEvent(
      new CustomEvent(SYNC_EVENT, {
        detail: { key, group: this.syncGroupValue, source: this.element },
      }),
    )
  }

  private handleSync(event: CustomEvent) {
    const { key, group, source } = event.detail ?? {}
    if (group !== this.syncGroupValue || source === this.element) return
    // Ignore keys this switcher doesn't offer; keep its current panel. A
    // switcher "offers" a key if it has a matching tab OR a matching panel —
    // the latter lets a control-less, panel-only switcher (e.g. a per-mode
    // chart whose tabs live in a shared headline elsewhere) follow the sync.
    const offered =
      this.tabTargets.some((t) => t.dataset.key === key) ||
      this.panelTargets.some((p) => p.dataset.key === key)
    if (!offered) return
    this.select(key, { animate: true })
  }

  private initialKey(): string | undefined {
    const active = this.tabTargets.find((t) => t.dataset.active === "true")
    return active?.dataset.key ?? this.tabTargets[0]?.dataset.key
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
  }

  private select(key: string | undefined, { animate }: { animate: boolean }) {
    if (!key || key === this.currentKey) return

    // Always cancel any in-flight crossfade first; it may leave a panel
    // mid-fade, so its cleanup resets opacity/transition before we proceed.
    this.fadeCleanup?.()
    this.fadeCleanup = undefined

    this.setTabs(key)
    this.setCaptions(key)

    const incoming = this.panelTargets.find((p) => p.dataset.key === key)
    const outgoing = this.panelTargets.find(
      (p) => p.dataset.key === this.currentKey,
    )
    this.currentKey = key

    if (!incoming) return

    if (!animate || this.prefersReducedMotion() || !outgoing || outgoing === incoming) {
      // Instant switch: show only the target panel, clear any stray styles.
      for (const panel of this.panelTargets) {
        panel.hidden = panel.dataset.key !== key
        panel.style.transition = ""
        panel.style.opacity = ""
      }
      return
    }

    this.crossfade(outgoing, incoming)
  }

  private crossfade(outgoing: HTMLElement, incoming: HTMLElement) {
    let timer: number | undefined

    // Idempotent finisher: leaves both panels in a clean steady state
    // (incoming visible at full opacity, all others hidden) no matter when
    // it's called — on natural completion, rapid re-click, or disconnect.
    const finish = () => {
      if (timer !== undefined) window.clearTimeout(timer)
      this.fadeCleanup = undefined
      for (const panel of this.panelTargets) {
        const on = panel === incoming
        panel.hidden = !on
        panel.style.transition = ""
        panel.style.opacity = ""
      }
    }
    this.fadeCleanup = finish

    // Phase 1: fade the outgoing panel to 0.
    outgoing.style.transition = `opacity ${FADE_MS}ms ease-out`
    outgoing.style.opacity = "1"
    // Force reflow so the starting opacity is committed before transitioning.
    void outgoing.offsetWidth
    outgoing.style.opacity = "0"

    timer = window.setTimeout(() => {
      // Phase 2: swap, then fade the incoming panel in from 0.
      outgoing.hidden = true
      outgoing.style.transition = ""
      outgoing.style.opacity = ""

      incoming.hidden = false
      incoming.style.transition = "none"
      incoming.style.opacity = "0"
      void incoming.offsetWidth
      incoming.style.transition = `opacity ${FADE_MS}ms ease-out`
      incoming.style.opacity = "1"

      timer = window.setTimeout(finish, FADE_MS)
    }, FADE_MS)
  }

  private setTabs(key: string) {
    for (const tab of this.tabTargets) {
      const on = tab.dataset.key === key
      tab.dataset.active = String(on)
      tab.setAttribute("aria-selected", String(on))
      tab.tabIndex = on ? 0 : -1
    }
  }

  // Swap each caption's text to the active key's copy, when provided. A caption
  // without a data-<key> for the current key keeps its existing text.
  private setCaptions(key: string) {
    if (this.captionTargets.length === 0) return
    for (const caption of this.captionTargets) {
      const text = caption.dataset[key]
      if (text != null) caption.textContent = text
    }
  }
}
